import { addDays, format, isAfter, parseISO } from 'date-fns';
import {
  type Availability,
  type RangeKey,
  TIME_RANGES,
  formatDateOnly,
  toDateOnly,
} from '../../../components/AvailabilityPicker/availability-utils';
import type { ReportItem } from '../execution-mode/types';
import { getItemExecutionMode } from '../execution-mode/utils';

export const SMART_LOCK_INSTALL_FEATURE_FLAG = 'igloo-smartlocks-enabled';

export const SMART_LOCK_REQUIRED_SLOTS = 3;

/** The install's cover image when it has no photos of its own. */
export const SMART_LOCK_INSTALL_IMAGE_URL =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/improvement-reports/smart_lock_installation.jpg';
const SMART_LOCK_TIME_FRAMES: RangeKey[] = ['Morning', 'Afternoon', 'Evening'];

/**
 * The picker's options. "All Day" is not sent as such: it picks every time
 * frame on its date, which alone fills the required slots.
 */
export const SMART_LOCK_TIME_OPTIONS: RangeKey[] = [
  'AllDay',
  ...SMART_LOCK_TIME_FRAMES,
];

type AvailabilitySlot = {
  date: string;
  time: RangeKey;
};

/** Loose shape of what the API returns (`AvailabilityTime`). */
export type RawAvailability = {
  date?: string | null;
  times?: RangeKey[] | null;
};

/** GET /maintenance/{id}/smart-lock-availability/window; both ends inclusive. */
export type SmartLockAvailabilityWindow = {
  firstDate: string;
  lastDate: string;
};

export type SmartLockInstallAvailabilityValue = {
  selections: Availability[];
  isDeferred: boolean;
};

// Guards against a malformed window producing an unbounded loop.
const MAX_WINDOW_DAYS = 366;

/** Every day (`yyyy-MM-dd`) between the window's first and last date, inclusive. */
export function getDatesInWindow(
  availabilityWindow: SmartLockAvailabilityWindow,
) {
  const first = parseISO(toDateOnly(availabilityWindow.firstDate));
  const last = parseISO(toDateOnly(availabilityWindow.lastDate));
  const dates: string[] = [];

  for (let i = 0; i < MAX_WINDOW_DAYS; i++) {
    const day = addDays(first, i);
    if (isAfter(day, last)) break;
    dates.push(format(day, 'yyyy-MM-dd'));
  }
  return dates;
}

const timeOrder = (time: RangeKey) => SMART_LOCK_TIME_FRAMES.indexOf(time);

const sortSlots = (slots: AvailabilitySlot[]) =>
  slots.sort(
    (a, b) =>
      a.date.localeCompare(b.date) || timeOrder(a.time) - timeOrder(b.time),
  );

const isFullDay = (day: Availability) =>
  SMART_LOCK_TIME_FRAMES.every((time) => day.times.includes(time));

const allDayOn = (date: string): Availability[] => [
  { date, times: [...SMART_LOCK_TIME_FRAMES] },
];

/** A slot is one time frame on one date, so a date with two times is two slots. */
export function countSlots(selections: Availability[]) {
  return selections.reduce((total, s) => total + s.times.length, 0);
}

export function flattenSlots(selections: Availability[]): AvailabilitySlot[] {
  return sortSlots(
    selections.flatMap((s) => s.times.map((time) => ({ date: s.date, time }))),
  );
}

/** Slots as the homeowner sees them: a date with every time frame is one "All Day". */
export function getDisplaySlots(
  selections: Availability[],
): AvailabilitySlot[] {
  return sortSlots(
    selections.flatMap((s) =>
      isFullDay(s)
        ? [{ date: s.date, time: 'AllDay' as const }]
        : s.times.map((time) => ({ date: s.date, time })),
    ),
  );
}

export function hasAllDay(selections: Availability[]) {
  return selections.some(isFullDay);
}

/**
 * Cleans API data into one entry per date, dates and times sorted, only the
 * install's time frames and no empty entries.
 */
export function normalizeSmartLockAvailability(
  raw: RawAvailability[] | null | undefined,
): Availability[] {
  const byDate = new Map<string, Set<RangeKey>>();

  (raw ?? []).forEach((entry) => {
    if (!entry?.date) return;
    const date = toDateOnly(entry.date);
    const times = (entry.times ?? []).filter((t) =>
      SMART_LOCK_TIME_FRAMES.includes(t),
    );
    if (!times.length) return;
    const existing = byDate.get(date) ?? new Set<RangeKey>();
    times.forEach((t) => existing.add(t));
    byDate.set(date, existing);
  });

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, times]) => ({
      date,
      times: [...times].sort((a, b) => timeOrder(a) - timeOrder(b)),
    }));
}

export function isSameAvailability(a: Availability[], b: Availability[]) {
  const key = (selections: Availability[]) =>
    flattenSlots(selections)
      .map((s) => `${s.date}|${s.time}`)
      .join(',');
  return key(a) === key(b);
}

/** Whether the slot shows as picked; a full day shows only as "All Day". */
export function hasSlot(selections: Availability[], slot: AvailabilitySlot) {
  const day = selections.find((s) => s.date === slot.date);
  if (!day) return false;
  if (slot.time === 'AllDay') return isFullDay(day);
  return !isFullDay(day) && day.times.includes(slot.time);
}

export function removeSlot(
  selections: Availability[],
  slot: AvailabilitySlot,
): Availability[] {
  return selections
    .map((s) =>
      s.date === slot.date
        ? {
            ...s,
            times:
              slot.time === 'AllDay'
                ? []
                : s.times.filter((t) => t !== slot.time),
          }
        : s,
    )
    .filter((s) => s.times.length > 0);
}

/**
 * Adds or removes a slot; adding is a no-op once `maxSlots` are picked.
 * "All Day" replaces every pick, and a time frame replaces a picked "All Day".
 */
export function toggleSlot(
  selections: Availability[],
  slot: AvailabilitySlot,
  maxSlots = SMART_LOCK_REQUIRED_SLOTS,
): Availability[] {
  if (hasSlot(selections, slot)) return removeSlot(selections, slot);
  if (slot.time === 'AllDay') return allDayOn(slot.date);
  if (hasAllDay(selections)) return [{ date: slot.date, times: [slot.time] }];
  if (countSlots(selections) >= maxSlots) return selections;

  const existing = selections.find((s) => s.date === slot.date);
  const next = existing
    ? selections.map((s) =>
        s.date === slot.date ? { ...s, times: [...s.times, slot.time] } : s,
      )
    : [...selections, { date: slot.date, times: [slot.time] }];

  return normalizeSmartLockAvailability(next);
}

/**
 * Choosing a day picks "All Day" on it, unless the homeowner is already
 * picking single time frames.
 */
export function selectDate(
  selections: Availability[],
  date: string,
): Availability[] {
  const [first] = selections;
  if (!first) return allDayOn(date);
  if (selections.length > 1 || !isFullDay(first)) return selections;
  return first.date === date ? selections : allDayOn(date);
}

export function formatSlotParts(slot: AvailabilitySlot) {
  const range = TIME_RANGES[slot.time];
  return {
    date: formatDateOnly(slot.date, 'EEEE, MMM d, yyyy'),
    time: `${range.label} ${range.timeRange}`,
  };
}

export function formatSlotLabel(slot: AvailabilitySlot) {
  const { date, time } = formatSlotParts(slot);
  return `${date} · ${time}`;
}

/** One label per saved slot, in date order, for the item's card in the report. */
export function getSmartLockCardSlotLabels(
  raw: RawAvailability[] | null | undefined,
): string[] {
  return getDisplaySlots(normalizeSmartLockAvailability(raw)).map((slot) => {
    const { timeRange } = TIME_RANGES[slot.time];
    return `${formatDateOnly(slot.date, 'EEEE, MMM d, yyyy')} · ${timeRange.replace(/[()]/g, '')}`;
  });
}

export function isSmartLockAvailabilityPending(item: ReportItem) {
  const homeownerPayment = item.maintenancePayments.find(
    (payment) => payment.paidBy === 'Homeowner',
  );
  const executionMode = getItemExecutionMode(item);
  return (
    !!item.maintenance.isAutomatedSmartLockInstallation &&
    (homeownerPayment?.percentage ?? 0) > 0 &&
    homeownerPayment?.consentStatus === 'Approved' &&
    !homeownerPayment.flagged &&
    executionMode !== 'HomeownerPro' &&
    executionMode !== 'HomeownerFixing' &&
    !normalizeSmartLockAvailability(item.maintenance.requestorAvailability)
      .length
  );
}

const DEFERRED_STORAGE_KEY_PREFIX = 'smart-lock-availability-deferred:';

export function isSmartLockAvailabilityDeferred(maintenanceUniqueId: string) {
  try {
    return (
      localStorage.getItem(
        `${DEFERRED_STORAGE_KEY_PREFIX}${maintenanceUniqueId}`,
      ) === 'true'
    );
  } catch {
    return false;
  }
}

export function setSmartLockAvailabilityDeferred(
  maintenanceUniqueId: string,
  isDeferred: boolean,
) {
  const key = `${DEFERRED_STORAGE_KEY_PREFIX}${maintenanceUniqueId}`;
  try {
    if (isDeferred) localStorage.setItem(key, 'true');
    else localStorage.removeItem(key);
  } catch {
    return;
  }
}

export function getPlaceholderText(remaining: number) {
  return remaining === 1 ? 'Pick 1 more time' : `Pick ${remaining} more times`;
}

/**
 * Saved slots outside the selectable dates are dropped, so the homeowner
 * re-picks them rather than resubmitting dates the backend would reject.
 * Without a window (it failed to load) the saved slots are kept untouched.
 */
export function getInitialSmartLockValue(
  raw: RawAvailability[] | null | undefined,
  selectableDates?: string[],
): SmartLockInstallAvailabilityValue {
  const saved = normalizeSmartLockAvailability(raw);
  return {
    selections: selectableDates
      ? saved.filter((s) => selectableDates.includes(s.date))
      : saved,
    isDeferred: false,
  };
}

export function isSmartLockAvailabilityComplete(
  value: SmartLockInstallAvailabilityValue,
) {
  return (
    !value.isDeferred &&
    countSlots(value.selections) === SMART_LOCK_REQUIRED_SLOTS
  );
}

/** Complete (3 slots) or explicitly deferred ("I don't know my availability yet"). */
export function isSmartLockAvailabilityResolved(
  value: SmartLockInstallAvailabilityValue,
) {
  return value.isDeferred || isSmartLockAvailabilityComplete(value);
}

/** Payload for PUT /maintenance/{id}/smart-lock-availability; `[]` clears it. */
export function buildSmartLockAvailabilityPayload(
  value: SmartLockInstallAvailabilityValue,
): Availability[] {
  if (value.isDeferred) return [];
  return normalizeSmartLockAvailability(value.selections);
}

/**
 * Whether the endpoint should be called: only for a resolved value that differs
 * from what is saved. A partial pick (allowed when "I want to discuss" is
 * checked) is never sent.
 */
export function shouldSaveSmartLockAvailability(
  value: SmartLockInstallAvailabilityValue,
  saved: RawAvailability[] | null | undefined,
) {
  if (!isSmartLockAvailabilityResolved(value)) return false;
  return !isSameAvailability(
    buildSmartLockAvailabilityPayload(value),
    normalizeSmartLockAvailability(saved),
  );
}

const GENERIC_ERROR_MESSAGE =
  'We could not save your availability. Please try again.';

export function getSmartLockAvailabilityErrorMessage(err: unknown): string {
  if (typeof err === 'string' && err.trim()) return err;
  if (Array.isArray(err) && typeof err[0]?.message === 'string') {
    return err[0].message;
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const { message } = err as { message?: unknown };
    if (typeof message === 'string' && message.trim()) return message;
  }
  return GENERIC_ERROR_MESSAGE;
}
